import React, { memo, useCallback } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import Button from '../Button/Button';
import { getToken } from '../../services/auth';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import ThirdPaneFooter from '../ThirdPane/ThirdPaneFooter';
import JobHistory from '../Job/JobHistory';
import { Box, Flex } from 'rebass';
import useNotify from '../../hooks/useNotify';
import useFormAssembly from '../../hooks/useFormAssembly';
import { Tester } from '../Tester/TesterPicker';
import JobDetail from './JobDetail';
import {
  ASSIGN_JOB,
  ASSIGN_JOB_WS,
  GET_FEE_AND_DEFAULT_PAYMENT_METHOD,
  GET_INBOUND_ASSIGNMENT_COMPANY,
  GET_INBOUND_ASSIGNMENT_INDIVIDUAL,
  GET_INBOUND_ASSIGNMENT_WATER_SUPPLIER,
  SET_ASSIGNMENT_STATUS,
} from './queries';
import { JobFormDataInput } from '@ecosconnect/core/src/types/jobFormData';
import { validateBackflowTest } from '@ecosconnect/core/src/helpers/backflow/validate';
import {
  AssignmentStatus,
  isCompletedStatus,
  isInReviewStatus,
  isOpenStatus,
} from '../../types/assignment';
import useMode from '../../hooks/useMode';
import EmailBackflowTestReportsModal from '../../pages/jobs/EmailBackflowTestReportsModal';
import { PortalWithState } from 'react-portal';
import SetJobStatusModal from './SetJobStatusModal';
import StripePaymentPortal from '../Stripe/StripePaymentPortal';
import { ResultOf } from 'gql.tada';
import { Merge } from '@/helpers/merge-type';
import RequirePerms from '@/components/RequirePerms';
import { useHasPerm } from '@/hooks/usePerms';
import { useJobFormData } from '../../hooks/useJobFormData';

type JobThirdPaneProps = {
  assignmentId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
  reload?: () => void;
};

const downloadPdf = (jobId?: string) => {
  if (!jobId) {
    return;
  }
  const url = `${
    import.meta.env.ECOS_API_BASE
  }/test-reports/${jobId}?token=${getToken()}`;
  window.open(url);
};

export type SetFormValue = <Key extends keyof JobFormDataInput>(
  field: Key,
  value:
    | JobFormDataInput[Key]
    | string
    | null
    | ((oldValue: JobFormDataInput[Key]) => JobFormDataInput[Key])
) => void;

export type SetFormValues = <Key extends keyof JobFormDataInput>(
  values:
    | Partial<JobFormDataInput>
    | Record<Key, string | number | undefined | null>
    | ((oldValues: JobFormDataInput) => Partial<JobFormDataInput>)
) => void;

export default memo(function JobThirdPane({
  assignmentId,
  reload,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: JobThirdPaneProps): JSX.Element {
  const { showError } = useNotify();
  const mode = useMode();
  const apolloClient = useApolloClient();
  const canUpdateAssignments = useHasPerm('assignments', 'UPDATE');

  const advanceAndReload = useCallback(() => {
    if (onNext && nextEnabled) {
      onNext();
    }
    if (reload) {
      reload();
    }
  }, [onNext, nextEnabled]);

  const GET_INBOUND_ASSIGNMENT =
    mode === 'company'
      ? GET_INBOUND_ASSIGNMENT_COMPANY
      : mode === 'water-supplier'
        ? GET_INBOUND_ASSIGNMENT_WATER_SUPPLIER
        : GET_INBOUND_ASSIGNMENT_INDIVIDUAL;

  type InboundAssignmentQueryType = Merge<
    Merge<
      ResultOf<typeof GET_INBOUND_ASSIGNMENT>,
      ResultOf<typeof GET_INBOUND_ASSIGNMENT_COMPANY>
    >,
    ResultOf<typeof GET_INBOUND_ASSIGNMENT_WATER_SUPPLIER>
  >;

  const {
    setFormValuesLocal,
    setJobId,
    formValues,
    setFormValue,
    setFormValues,
    onFormInputChange,
  } = useJobFormData<JobFormDataInput>();
  const { refetch, data } = useQuery<InboundAssignmentQueryType>(
    GET_INBOUND_ASSIGNMENT,
    {
      skip: !assignmentId,
      variables: { where: { id: assignmentId } },
      fetchPolicy: 'network-only',
      onCompleted(data) {
        const newAssignment = data?.inboundAssignments[0];
        if (newAssignment?.job) {
          setJobId(newAssignment.job.id);
          setFormValuesLocal(newAssignment.job.formData as JobFormDataInput);
        }
      },
      onError: (error) => {
        showError('Unable to load job', error);
      },
    }
  );

  const assignment = data?.inboundAssignments?.[0];
  const rpCheck2Type =
    assignment?.job?.backflowTest?.waterSupplier?.rpCheck2Type ?? 'USC10';

  const downloadReport = useCallback(() => {
    if (assignment) {
      downloadPdf(assignment.jobId);
    }
  }, [assignment?.jobId]);

  const [setAssignmentStatus, { loading: statusMutationLoading }] = useMutation<
    {
      setInboundAssignmentStatus: {
        id: string;
        status: AssignmentStatus;
        jobId: string;
        completed: number;
        submitted: number;
        job: {
          id: string;
        };
      }[];
    },
    {
      assignmentId: string;
      newStatus: AssignmentStatus;
      oldStatus: AssignmentStatus;
    }
  >(SET_ASSIGNMENT_STATUS, {
    onCompleted: () => {
      reload?.();
      refetch();
    },
  });

  const completeJob = useCallback(async () => {
    if (!assignment || !formValues) {
      return;
    }
    const result = validateBackflowTest(formValues, rpCheck2Type);
    if (!result.valid) {
      showError('Unable to set job to in review', result.errors.join(', '));
      return;
    }

    await setAssignmentStatus({
      variables: {
        assignmentId: assignment.id,
        newStatus: 'COMPLETED',
        oldStatus: assignment.status,
      },
      onError: (error) => {
        showError('Error setting job to in review', error);
      },
    });
  }, [assignment, formValues, rpCheck2Type]);

  const lacksRequiredPaymentMethod = useCallback(async () => {
    if (mode !== 'company') {
      return false;
    }
    const result = await apolloClient.query<{
      stripePlatformCustomer: {
        defaultPaymentMethodId?: string;
      };
      inboundAssignments: {
        id: string;
        vendorSubmissionFee: number;
      }[];
    }>({
      query: GET_FEE_AND_DEFAULT_PAYMENT_METHOD,
      variables: {
        assignmentId: assignment?.id,
      },
      fetchPolicy: 'network-only',
    });

    if (result.data?.inboundAssignments[0]?.vendorSubmissionFee === 0) {
      return false;
    }

    return !result.data?.stripePlatformCustomer?.defaultPaymentMethodId;
  }, [mode, assignment?.id]);

  const submitJob = useCallback(async () => {
    if (!assignment || !formValues) {
      return;
    }
    if (mode !== 'individual' && !assignment.child) {
      showError(
        'Unable to submit job',
        'Please assign the job before submitting'
      );
      return;
    }

    const result = validateBackflowTest(formValues, rpCheck2Type);
    if (!result.valid) {
      showError('Unable to submit job', result.errors.join(', '));
      return;
    }

    await setAssignmentStatus({
      variables: {
        assignmentId: assignment.id,
        newStatus: 'SUBMITTED',
        oldStatus: assignment.status,
      },
      onError: (error) => {
        showError('Error submitting job', error);
      },
    });
  }, [assignment, formValues, rpCheck2Type]);

  const unsubmitJob = useCallback(async () => {
    if (!assignment?.canUnsubmit) {
      return;
    }

    await setAssignmentStatus({
      variables: {
        assignmentId: assignment.id,
        newStatus: 'ASSIGNED',
        oldStatus: assignment.status,
      },
      onError: (error) => {
        showError('Error unsubmitting job', error);
      },
    });
  }, [assignment, setAssignmentStatus, showError]);

  const formAssembly = useFormAssembly(
    formValues,
    formValues
      ? (assignment?.job?.backflowTest?.backflowAssembly ?? undefined)
      : undefined
  );

  const [assignJob] = useMutation(
    mode === 'water-supplier' ? ASSIGN_JOB_WS : ASSIGN_JOB,
    {
      refetchQueries: [GET_INBOUND_ASSIGNMENT],
    }
  );
  const onTesterChange = useCallback(
    (tester: Tester | null) => {
      if (!tester) {
        return;
      }
      assignJob({
        variables: {
          assignmentId: assignmentId!,
          vendorProfileId: tester.profileId,
        },
        onError: (error) => {
          showError('Error assigning job', error);
        },
      });
    },
    [assignJob, assignmentId]
  );

  const onNestedTesterChange = useCallback(
    (tester: Tester | null) => {
      const vendorProfileId = assignment?.child?.vendorProfile?.id;

      if (!tester || !vendorProfileId) {
        return;
      }
      assignJob({
        variables: {
          assignmentId: assignmentId!,
          vendorProfileId,
          testerProfileId: tester.profileId,
        },
        onError: (error) => {
          showError('Error assigning job', error);
        },
      });
    },
    [assignJob, assignmentId, assignment?.child?.vendorProfile]
  );
  return (
    <ThirdPane visible={!!assignmentId}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={assignment ? assignment?.jobId : 'Loading...'}
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      />
      {/************************************
       *
       * Content
       *
       ************************************/}
      <ThirdPaneContent className="content">
        {assignment?.job && (
          <Flex flexDirection="row" height="100%" flexGrow={1} flexShrink={1}>
            {/************************************
             * Column 1
             * ? should this whole thing move into the job detail view?
             ************************************/}
            <Flex
              flexDirection="column"
              flexGrow={1}
              flexShrink={1}
              height="100%"
              width={750}
              overflowY="scroll"
            >
              {assignment?.job?.backflowTest && (
                <JobDetail
                  action={
                    canUpdateAssignments && isOpenStatus(assignment.status)
                      ? 'EDIT'
                      : 'VIEW'
                  }
                  customer={assignment.customer}
                  vendorProfile={assignment.child?.vendorProfile}
                  // nestedTesterProfile={assignment.child?.child?.vendorProfile}
                  backflowTest={assignment.job.backflowTest}
                  job={assignment.job}
                  formData={formValues}
                  formAssembly={formAssembly}
                  setFormValue={setFormValue}
                  setFormValues={setFormValues}
                  onFormInputChange={onFormInputChange}
                  onTesterChange={
                    canUpdateAssignments &&
                    (mode === 'company' || mode === 'water-supplier')
                      ? onTesterChange
                      : undefined
                  }
                  onNestedTesterChange={
                    canUpdateAssignments && mode === 'water-supplier'
                      ? onNestedTesterChange
                      : undefined
                  }
                  refetch={refetch}
                />
              )}
            </Flex>

            {/************************************
             * Column 2
             ************************************/}
            <JobHistory
              width={325}
              sx={{ borderLeft: '1px solid', borderLeftColor: 'tableBorder' }}
              jobId={assignment.jobId}
              comments={assignment.job.comments}
              statusChanges={assignment.job.statusChanges}
              attachments={assignment.job.attachments}
              jobAddress={assignment.job.address}
              refetch={refetch}
              waterSupplierName={
                assignment.job.backflowTest?.waterSupplier?.profile.name
              }
              waterSupplierProfileId={
                assignment.job.backflowTest?.waterSupplier?.profileId
              }
            />
          </Flex>
        )}
      </ThirdPaneContent>
      {/************************************
       *
       * Footer
       *
       ************************************/}
      {assignment?.job?.backflowTest && (
        <ThirdPaneFooter>
          <Flex flexGrow={1} sx={{ gap: 2 }} textAlign="left">
            <RequirePerms assignments="UPDATE">
              <>
                {mode !== 'water-supplier' &&
                  isOpenStatus(assignment.status) &&
                  assignment.status !== 'COMPLETED' && (
                    <Button onClick={completeJob} icon="check-circle">
                      Set to In Review
                    </Button>
                  )}
                {isInReviewStatus(assignment.status) && assignment.child && (
                  <PortalWithState closeOnEsc>
                    {({ openPortal, closePortal, portal }) => (
                      <>
                        <Button icon="repeat" onClick={openPortal}>
                          Request Follow-Up
                        </Button>
                        {portal(
                          <SetJobStatusModal
                            assignmentIds={[assignment.id]}
                            status="FOLLOW_UP"
                            onClose={(actionTaken) => {
                              if (actionTaken) {
                                reload?.();
                              }
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </PortalWithState>
                )}
                {isOpenStatus(assignment.status) && (
                  <PortalWithState closeOnEsc>
                    {({ openPortal, closePortal, portal }) => (
                      <>
                        <Button
                          onClick={async () => {
                            if (await lacksRequiredPaymentMethod()) {
                              openPortal();
                            } else {
                              submitJob();
                            }
                          }}
                          icon="check-circle"
                        >
                          Submit
                        </Button>

                        {portal(
                          <StripePaymentPortal
                            message={`There is a fee of $${assignment.vendorSubmissionFee.toFixed(2)} to submit this job. Please add a payment to proceed.`}
                            onCloseComplete={async () => {
                              if (!(await lacksRequiredPaymentMethod())) {
                                submitJob();
                              }
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </PortalWithState>
                )}
                {mode === 'company' && isCompletedStatus(assignment.status) && (
                  <PortalWithState closeOnEsc>
                    {({ openPortal, closePortal, portal }) => (
                      <>
                        <Button
                          variant="outline"
                          icon="mail"
                          onClick={openPortal}
                        >
                          Email Test Report
                        </Button>
                        {portal(
                          <EmailBackflowTestReportsModal
                            assignmentIds={[assignment.id]}
                            onClose={(actionTaken) => {
                              closePortal();
                              if (actionTaken) {
                                refetch();
                                reload?.();
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  </PortalWithState>
                )}
                {mode !== 'water-supplier' && assignment.canUnsubmit && (
                  <Button
                    variant="secondary"
                    icon="undo"
                    onClick={unsubmitJob}
                    disabled={statusMutationLoading}
                  >
                    {statusMutationLoading ? 'Unsubmitting...' : 'Unsubmit'}
                  </Button>
                )}
              </>
            </RequirePerms>
          </Flex>
          <Box flexGrow={1} textAlign="right">
            <Button
              variant="secondary"
              onClick={downloadReport}
              icon="file-download"
            >
              PDF
            </Button>
          </Box>
        </ThirdPaneFooter>
      )}
    </ThirdPane>
  );
});
